import React from "react";
import { Autocomplete, Checkbox, TextField } from "@mui/material";
import { Controller } from "react-hook-form";
import { matchSorter } from "match-sorter";
import { optionKey, withoutKey } from 'app/main/apps/common/optionProps';

/*
 * Multi-select Controlled Autocomplete
 * with optional Check Box
 */
const AuraControlledMultiSelectAutocomplete = ({
  controllerName, // controller field name
  control, // controller
  options = [], // list items
  required = false, // required field
  disabled = false, // Enable/disable autocomplete
  hideClearable = false, // Enable/disable autocomplete clear icon
  label = "Select Items", // autocomplete label
  showSelectAll = true, // Enable/Disable Select All options in the top of the list
  selectAllLabel = "All Selected", // this will be displayed in the autocomplete input section when all list item is selected
  optionDisplayName = "name", // List item
  limitTags = 10, // No items to be displayed in the autocomplete input section
  renderTags = null, // pass to change default render tags
  renderOption = null, // pass to change default render options
  filterOptions = null, // pass to change default filter options
  searchFields = ["name"], // List search fields
  size = "small", // sixe of autocomplete
  listItemStyle = null, // List item style. withSecondaryText = Displays secondary text in the second line
  optionSubDisplayName = "email", // What item should display in secondary item place
  showListItemCheckBox = true, // To display checkbox in the list item
  classNames = {
    controllerClassName: {},
    autocompleteClassName: {},
    textFieldClassName: {},
  }, // Overall className styles
  sxStyles = {
    controllerSx: {},
    autocompleteSx: {},
    textFieldSx: {
      "& .MuiAutocomplete-input": {
        textTransform: "capitalize",
      },
    },
  }, // Overall sx styles
}) => {
  return (
    <Controller
      name={controllerName}
      control={control}
      rules={{ required }}
      className={classNames?.controllerClassName}
      sx={sxStyles?.controllerSx}
      render={({ field }) => {
        const allOption = { id: "all", name: "Select All" };
        const optionList = showSelectAll ? [allOption, ...options] : options;

        const isAllSelected = field?.value?.length === options?.length;

        const handleChange = (_, selectedOptions) => {
          const includesAll = selectedOptions.some((opt) => opt.id === "all");

          if (includesAll && showSelectAll) {
            if (isAllSelected) {
              field.onChange([]);
            } else {
              field.onChange([...options]);
            }
          } else {
            const filtered = selectedOptions.filter((opt) => opt.id !== "all");
            field.onChange(filtered);
          }
        };

        // Default renderOption if none provided
        const defaultRenderOption = (props, option, { inputValue }) => {
          if (typeof props === "string") return <li key={optionKey(props)} {...withoutKey(props)}>{option}</li>;

          if (listItemStyle === "withSecondaryText") {
            const isChecked =
              option.id === "all"
                ? isAllSelected
                : Array.isArray(field.value) &&
                  field?.value?.some((val) => val.id === option.id);

            return (
              <li
                {...withoutKey(props)}
                key={option.id}
                className={`p-4 flex flex-col cursor-pointer hover:bg-gray-100 ${
                  isChecked && "!bg-[#e3f2fd]"
                }`}
              >
                <div
                  className={`flex flex-row ${option.id === "all" && "mb-10"}`}
                >
                  {showListItemCheckBox && (
                    <Checkbox className="p-0" checked={isChecked} />
                  )}
                  <span
                    className={`${
                      option.id === "all" && "font-medium text-indigo"
                    } cursor-pointer ml-8`}
                  >
                    {option[optionDisplayName]}
                  </span>
                </div>
                {option.id !== "all" && (
                  <div className="flex flex-row">
                    <span
                      className={`${
                        showListItemCheckBox ? "pl-32" : "ml-8"
                      } text-grey cursor-pointer`}
                    >
                      {option[optionSubDisplayName]}
                    </span>
                  </div>
                )}
              </li>
            );
          }
          return <li key={optionKey(props)} {...withoutKey(props)}>{option[optionDisplayName]}</li>;
        };

        // Default filterOption if none provided
        const defaultFilterOptions = (optionsFP, { inputValue }) => {
          const filtered = matchSorter(optionsFP, inputValue, {
            keys: searchFields,
            threshold: matchSorter.rankings.CONTAINS,
          });

          if (showSelectAll) {
            const selectAll = optionsFP.find((opt) => opt.id === "all");
            return selectAll
              ? [selectAll, ...filtered.filter((opt) => opt.id !== "all")]
              : filtered;
          }
          return filtered;
        };

        // Default renderTags if none provided
        const defaultRenderTags = (value, getTagProps) => {
          if (value.length === options.length && showSelectAll) {
            return <span style={{ paddingLeft: 4 }}>{selectAllLabel}</span>;
          }
          return value
            .slice(0, limitTags)
            .map((option, index) => (
              <div
                key={option.id}
                {...getTagProps({ index })}
                style={{
                  backgroundColor: "#e3f2fd",
                  padding: "2px 6px",
                  marginRight: 4,
                  borderRadius: 4,
                }}
              >
                {option[optionDisplayName]}
              </div>
            ))
            .concat(
              value.length > limitTags ? (
                <div style={{ paddingLeft: 4 }} key="extra">
                  +{value.length - limitTags} more
                </div>
              ) : null
            );
        };

        return (
          <Autocomplete
            multiple
            options={optionList}
            value={field.value || []}
            getOptionLabel={(option) => option?.[optionDisplayName] || ""}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            disableCloseOnSelect
            disabled={disabled}
            disableClearable={hideClearable}
            onChange={handleChange}
            filterOptions={filterOptions || defaultFilterOptions}
            renderTags={renderTags || defaultRenderTags}
            renderOption={renderOption || defaultRenderOption}
            className={classNames?.autocompleteClassName}
            sx={sxStyles?.autocompleteSx}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                size={size}
                fullWidth
                className={classNames?.textFieldClassName}
                sx={sxStyles?.textFieldSx}
              />
            )}
          />
        );
      }}
    />
  );
};

export default AuraControlledMultiSelectAutocomplete;
